import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BenAdminRenewal, BenAdminRenewalUpdatableProps, BenAdminRenewalEmployeeContributions } from "BenAdminRenewal/types";
import companyToken from "Utils/companyToken";
import { prepareHeadersWithCSRFToken } from "Utils/fetchWithCSRFToken";
import { CompanyPolicy } from "Utils/types";

import { appendAuthParams } from "Common/TokenAuth/Utils";

type EmployeeUpdateProps = {
  externalID: string;
} & BenAdminRenewalEmployeeContributions;

type EmployeeContributionsUpdateProps = {
  employees: EmployeeUpdateProps[];
};

export const benAdminRenewalApi = createApi({
  reducerPath: "benAdminRenewal",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v2",
    prepareHeaders: prepareHeadersWithCSRFToken,
  }),

  endpoints: (builder) => ({
    getBenAdminRenewal: builder.query<BenAdminRenewal, null>({
      query: () => {
        const companyIDToken = companyToken();
        const data = {
          ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
        };
        const params = new URLSearchParams(data).toString();
        return appendAuthParams(`/ben_admin_renewal?${params}`);
      },
      providesTags: ["BenAdminRenewal"],
    }),
    updateBenAdminRenewal: builder.mutation<BenAdminRenewal, BenAdminRenewalUpdatableProps>({
      query: (values) => {
        const companyIDToken = companyToken();
        return {
          url: appendAuthParams("/ben_admin_renewal"),
          method: "PUT",
          body: {
            ben_admin_renewal: values,
            ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
          },
        };
      },
      invalidatesTags: ["BenAdminRenewal"],
    }),
    updateEmployeeContributions: builder.mutation<BenAdminRenewal, EmployeeContributionsUpdateProps>({
      query: (values) => {
        const companyIDToken = companyToken();
        return {
          url: appendAuthParams("/ben_admin_renewal/employee_contributions"),
          method: "PUT",
          body: {
            ben_admin_renewal: values,
            ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
          },
        };
      },
      invalidatesTags: ["BenAdminRenewal"],
    }),
  }),
  tagTypes: ["BenAdminRenewal"],
});

export const { useGetBenAdminRenewalQuery, useUpdateBenAdminRenewalMutation, useUpdateEmployeeContributionsMutation } = benAdminRenewalApi;

/**
 * Keeping these Dental/Vision apis here for now to avoid a hasty abstraction.
 * This is the same api used by the EmployerRenewalApplication.
 * This API returns recommended plans plus the plans in the policy with costs.
 * I believe we can put this in the Features folder, but we need to have a
 * clearer understanding of how to split Plan data from Costs / Quotes data.
 */
type DentalPlansResponseBody = {
  results: DentalPlanPrice[];
  num_results: number;
};

type CompanyPolicyID = string;

export const dentalPlansApi = createApi({
  reducerPath: "dentalPlans",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v2",
    prepareHeaders: prepareHeadersWithCSRFToken,
  }),

  endpoints: (builder) => ({
    getDentalPlans: builder.query<DentalPlansResponseBody, CompanyPolicyID>({
      query: (companyPolicyId) => {
        return appendAuthParams(`/company_policies/${companyPolicyId}/dental_plans`);
      },
      providesTags: ["DentalPlans"],
    }),
  }),
  tagTypes: ["DentalPlans"],
});

export const { useGetDentalPlansQuery } = dentalPlansApi;

type VisionPlansResponseBody = {
  results: VisionPlanPrice[];
  num_results: number;
};

export const visionPlansApi = createApi({
  reducerPath: "visionPlans",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v2",
    prepareHeaders: prepareHeadersWithCSRFToken,
  }),

  endpoints: (builder) => ({
    getVisionPlans: builder.query<VisionPlansResponseBody, CompanyPolicyID>({
      query: (companyPolicyId) => {
        return appendAuthParams(`/company_policies/${companyPolicyId}/vision_plans`);
      },
      providesTags: ["VisionPlans"],
    }),
  }),
  tagTypes: ["VisionPlans"],
});

export const { useGetVisionPlansQuery } = visionPlansApi;

// EmployerRenewalApplicationApi
// These are the current actions used by the EmployerRenewalApplication.
// There are several side-effects that happen in them.
// Wrapping them in rtk-query to avoid using legacy EntitiesAPI and other
// renewal application store dependencies.
// This could potentially be abstracted into the Feature folder in the future.
type SignRenewalApplicationBody = {
  id: string;
} & SignatureProp;

export const employerRenewalApplicationApi = createApi({
  reducerPath: "employerRenewalApplication",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v1",
    prepareHeaders: prepareHeadersWithCSRFToken,
  }),

  endpoints: (builder) => ({
    signRenewalApplication: builder.mutation<CompanyPolicy, SignRenewalApplicationBody>({
      query: (body) => ({
        method: "POST",
        body,
        url: "/employer_application/sign_application",
      }),
    }),
  }),
});

export const { useSignRenewalApplicationMutation } = employerRenewalApplicationApi;
